/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { domainsCreateDomain } from "../funcs/domainsCreateDomain.js";
import { domainsDeleteDomain } from "../funcs/domainsDeleteDomain.js";
import { domainsGetDomain } from "../funcs/domainsGetDomain.js";
import { domainsListDomains } from "../funcs/domainsListDomains.js";
import { domainsVerifyDomain } from "../funcs/domainsVerifyDomain.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import * as components from "../models/components/index.js";
import { unwrapAsync } from "../types/fp.js";

export class Domains extends ClientSDK {
  /**
   * Create domain
   *
   * @remarks
   * Attach a custom domain to an environment and start verifying it.
   *
   * The domain is created in the `pending` state and does not serve traffic until verification succeeds. Verification runs in the background and polls DNS, so it is eventually consistent.
   *
   * The response returns `dnsRecords`: every record needed to finish setup, already resolved for whether this domain is an apex or a subdomain. Create every entry exactly as given. One record establishes routing and one proves ownership, and both are needed: whether ownership can be inferred from the routing record depends on how your provider publishes it, and a name another workspace has already verified can only be claimed through the ownership record. Neither is knowable before the records exist.
   *
   * When your DNS provider supports Domain Connect, the response also carries a `domainConnect` object; opening its `url` applies the same records at the provider in one step. The object is absent when the shortcut is unavailable.
   *
   * Domains are unique per workspace, so the same name cannot be attached to two environments. Attaching a domain that already exists in your workspace returns a 409 conflict.
   *
   * How many domains you may attach is set by your plan. Attaching one beyond that allowance returns a 403; upgrade the plan or remove a domain you no longer need.
   *
   * **Important**: verification stops after 24 hours without the required DNS records, and the domain moves to `failed`.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `environment.*.create_domain` (to attach domains to any environment)
   * - `environment.<environment_id>.create_domain` (to attach domains to a specific environment)
   */
  async createDomain(
    request: components.V1DomainsCreateDomainRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1DomainsCreateDomainResponseBody> {
    return unwrapAsync(domainsCreateDomain(
      this,
      request,
      options,
    ));
  }

  /**
   * Delete domain
   *
   * @remarks
   * Delete a custom domain from your workspace.
   *
   * Address the domain by its id or by its name. Names are unique per workspace, so
   * `api.acme.com` is enough.
   *
   * Teral stops serving the domain. Later requests fail with a certificate error. The DNS
   * records at your provider stay in place.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `environment.*.delete_domain` (to delete domains in any environment)
   * - `environment.<environment_id>.delete_domain` (to delete domains in a specific environment)
   */
  async deleteDomain(
    request: components.V1DomainsDeleteDomainRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1DomainsDeleteDomainResponseBody> {
    return unwrapAsync(domainsDeleteDomain(
      this,
      request,
      options,
    ));
  }

  /**
   * Get domain
   *
   * @remarks
   * Retrieve a custom domain and its verification status.
   *
   * Address the domain by its id or by its name. Names are unique per workspace, so
   * `api.acme.com` is sufficient. You do not need to supply a project, app, or environment.
   *
   * Use this endpoint to poll after `domains.createDomain`. Verification runs in the background
   * and checks DNS approximately each minute.
   *
   * `status: verified` means the domain is verified. Teral has configured routing and requested a
   * certificate. Each entry in `dnsRecords` has a `verified` flag. The flag shows which records
   * Teral has read back, so you can see which records are still missing. Some providers hide a
   * record from DNS lookups, for example a proxied or flattened routing record. Such a record stays
   * `false` while it serves traffic. `verificationError` gives the reason for the last failed
   * attempt.
   *
   * `dnsRecords` contains the same values that `domains.createDomain` returned. Use it to recover
   * the values without creating the domain again.
   *
   * **Important**: verification stops 24 hours after the domain was created, and the status becomes
   * `failed`. The window starts at `createdAt`, not at the last attempt.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `environment.*.read_domain` (to read domains in any environment)
   * - `environment.<environment_id>.read_domain` (to read domains in a specific environment)
   */
  async getDomain(
    request: components.V1DomainsGetDomainRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1DomainsGetDomainResponseBody> {
    return unwrapAsync(domainsGetDomain(
      this,
      request,
      options,
    ));
  }

  /**
   * List domains
   *
   * @remarks
   * List your custom domains with their verification status and DNS records.
   * Filter by project, app, or environment using IDs or slugs, or send `{}` to list
   * domains across your workspace.
   *
   * Use any filter on its own or combine filters to narrow the results.
   * Results match all supplied filters. Omitting `environment` includes all matching environments.
   *
   * Results include only domains you have permission to read, sorted by ID.
   * When `hasMore` is true, send the returned `cursor` to get the next page.
   *
   * `status: verified` means the domain is verified. Teral has configured routing and requested a
   * certificate. Each domain includes its full `dnsRecords`. Each record has a `verified` flag.
   * The flag shows which records Teral has read back, so you can see which records are still
   * missing without a second call. Some providers hide a record from DNS lookups, for example a
   * proxied or flattened routing record. Such a record stays `false` while it serves traffic.
   *
   * **Required Permissions**
   *
   * Use a root key with the `environment.*.read_domain` permission.
   * A successful request returns an empty list if no matching domains are readable by your key.
   */
  async listDomains(
    request: components.V1DomainsListDomainsRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1DomainsListDomainsResponseBody> {
    return unwrapAsync(domainsListDomains(
      this,
      request,
      options,
    ));
  }

  /**
   * Verify domain
   *
   * @remarks
   * Restart verification for a custom domain.
   *
   * Address the domain by its ID or by its name. Names are unique per workspace, so
   * `api.acme.com` is enough.
   *
   * Call this after you correct the DNS records of a domain that shows `failed`, or to give a
   * `pending` domain a new 24-hour verification period. The domain goes back to `pending` and
   * the 24-hour period starts again.
   *
   * The endpoint returns when Teral accepts the retry. Poll `domains.getDomain` for the result.
   *
   * A domain that is already `verified` returns a 412.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `environment.*.verify_domain` (to verify domains in any environment)
   * - `environment.<environment_id>.verify_domain` (to verify domains in a specific environment)
   */
  async verifyDomain(
    request: components.V1DomainsVerifyDomainRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1DomainsVerifyDomainResponseBody> {
    return unwrapAsync(domainsVerifyDomain(
      this,
      request,
      options,
    ));
  }
}
